import { useState } from 'react';

import {
  useAllObjectClassListQuery,
  useGetExtendClassIconListQuery,
} from '@/hook';
import {
  Check,
  Tune,
  VBasePopover,
  VDropdown,
  VFlex,
  VIconButton,
  VTooltip,
  new__cn as cn,
} from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useTaskFrameStore, useToolPermissionStore } from '@/store';

import { Icon } from './IconPopover';
import ToolBoxInspector from './ToolBoxInspector';

const LabelOperation = () => {
  const { selectedLabelingBoxList: selectedBoxList } = useLabelingBoxContext();
  const selectedBox = selectedBoxList?.[0];

  return (
    <div className="absolute top-2 left-1/2 -translate-x-1/2 transform">
      {selectedBox && <ObjectInfo />}
    </div>
  );
};

// const SelectLabel = () => {
//   const { toolMap, findByName, selectChildren, isSelectedByToolName } =
//     useToolMenuStore();
//   const { selectedTask } = useTaskFrameStore();
//   const genBoxTool = useMemo(() => findByName('box'), [toolMap]);
//   const { data: allObjectClassList } = useAllObjectClassListQuery();
//   const enabledLabelItemList = (
//     selectedTask?.extendClass ??
//     allObjectClassList ??
//     []
//   ).filter(item => item.enable);

//   return (
//     <VFlex className="w-fit gap-[2px] rounded-sm bg-gray-200 p-[2px]">
//       {enabledLabelItemList?.map(({ id, icon, name, color }) => {
//         const toolChild = genBoxTool?.children.find(c => c.name === `box${id}`);
//         const isSelected = toolChild?.name
//           ? isSelectedByToolName(toolChild.name)
//           : false;
//         const selectedColor = new RGBA(color).toString();

//         return (
//           <VFlex
//             key={id}
//             className={cn('rounded-xs px-2 py-1', {
//               'bg-primary-500': isSelected,
//               'bg-white': !isSelected,
//             })}
//             gap={0.25}
//             role="button"
//             onClick={() => {
//               if (genBoxTool && toolChild) {
//                 selectChildren(genBoxTool.id, toolChild.id);
//               }
//             }}
//             items="center"
//           >
//             <Add
//               className={cn('h-4 w-4', {
//                 'text-white': isSelected,
//                 'text-gray-400': !isSelected,
//               })}
//             />
//             <VFlex
//               className="gap-[2px]"
//               items="center"
//               style={{
//                 color: isSelected ? selectedColor : '#0B1B3E',
//               }}
//             >
//               <Icon data={icon?.data ?? ''} />
//               <div className="text-body-md">{startCase(name)}</div>
//             </VFlex>
//           </VFlex>
//         );
//       })}
//     </VFlex>
//   );
// };

const ObjectInfo = () => {
  const { selectedLabelingBoxList: selectedBoxList, changeBoxLabel } =
    useLabelingBoxContext();
  const selectedBox = selectedBoxList?.at(0);
  const labelValue = Number(selectedBox?.label ?? '1');
  const { data: allObjectClassList } = useAllObjectClassListQuery();
  const { selectedTask } = useTaskFrameStore();
  const enabledLabelItemList = (
    selectedTask?.extendClass ??
    allObjectClassList ??
    []
  ).filter(item => item.enable);
  const [inspectorVisible, setInspectorVisible] = useState(false);
  const closeInspector = () => setInspectorVisible(false);
  const { permission } = useToolPermissionStore();
  const labelItemList = enabledLabelItemList.map(item => ({
    value: Number(item.id),
    label: item.alias || item.name,
  }));
  const { data: iconList } = useGetExtendClassIconListQuery();
  const [labelItemListVisible, setLabelItemListVisible] = useState(false);
  const isLabelUpdatable = permission.labeling.updatable;

  return (
    <VFlex
      items="center"
      className="w-fit gap-[2px] rounded-sm bg-white p-[2px]"
    >
      <VDropdown
        // TODO: 타입 수정
        itemList={
          isLabelUpdatable
            ? labelItemList
            : labelItemList.filter(item => +item.value === +labelValue)
        }
        itemListClassName={cn('max-w-[146px]')}
        itemListHeight={232}
        value={labelValue}
        variant={labelItemListVisible ? 'primary' : 'transparent'}
        onItemListVisibleChange={visible => setLabelItemListVisible(visible)}
        disabled={!isLabelUpdatable}
        renderItem={({ item }) => {
          const iconData =
            iconList?.find(
              icon =>
                +icon.id ===
                allObjectClassList?.find(
                  objectClass => +objectClass.id === +item.value,
                )?.iconID,
            )?.data ?? '';

          const color = enabledLabelItemList.find(
            objectClass => +objectClass.id === +item.value,
          )?.color;

          return (
            <VFlex gap={0.5} items="center" className="w-[120px]">
              <Check
                className={cn(
                  'h-5 w-5 shrink-0',
                  'text-blue-300',
                  item.value !== labelValue && 'opacity-0',
                )}
              />
              <VFlex
                className="min-w-0 flex-1 text-primary-500"
                items="center"
                gap={0.25}
              >
                <div className="shrink-0">
                  <Icon data={iconData} color={color} />
                </div>
                <VTooltip content={item.label}>
                  <div className="min-w-0 flex-1 truncate text-body-md font-semibold capitalize">
                    {item.label}
                  </div>
                </VTooltip>
              </VFlex>
            </VFlex>
          );
        }}
        onItemClick={e => {
          changeBoxLabel(
            // selectedBox!.id.value!,
            selectedBox!.id,
            e.target!.item.value.toString(),
          );
        }}
      >
        <VFlex className="w-[100px] text-xs " gap={0.125} items="center">
          <Icon
            data={
              iconList?.find(
                icon =>
                  +icon.id ===
                  enabledLabelItemList.find(item => +item.id === +labelValue)
                    ?.iconID,
              )?.data ?? ''
            }
            color={
              enabledLabelItemList.find(item => +item.id === +labelValue)?.color
            }
          />
          <span className="translate-y-px">
            {labelItemList.find(item => +item.value === +labelValue)?.label ??
              ''}
          </span>
        </VFlex>
      </VDropdown>

      <VFlex justify="center" items="center">
        <VBasePopover
          className="-translate-y-scale/2 rounded-md"
          content={() => <ToolBoxInspector onClose={closeInspector} />}
          placement="right-top"
          trigger="click"
          visible={inspectorVisible}
          onUpdateVisible={visible => setInspectorVisible(visible)}
        >
          <VIconButton
            Icon={Tune}
            role="button"
            outlined={!inspectorVisible}
            size="md"
            variant={inspectorVisible ? 'primary' : 'roundPrimary'}
          />
        </VBasePopover>
      </VFlex>
    </VFlex>
  );
};

export default LabelOperation;
